/**
 * dashboard-tiles · the six locked Blueprint surface tiles for the dashboard.
 *
 * Locked positioning hybrid (Russ 2026-05-04, captured at
 * /shared/V1.x_Conflict_Mediation_Design_Recommendations.md §4):
 *   - Conflict Mediation occupies index 0 (top-left, most-emphasized slot)
 *     and carries the NEW badge for the V1.0 launch window
 *   - The other five surfaces fill remaining slots in their existing order
 *
 * Each entry's `featureKey` MUST exist in the client FEATURE_MIN_TIER map
 * at /src/lib/feature-gates.js (which mirrors the server-side map at
 * /netlify/functions/_lib/feature-flags.js). The tier-gate sync test
 * at /tests/feature-gates-cm-tier-sync.test.js guards against client-side
 * drift; the dashboard-tiles-shape test at
 * /tests/dashboard-tiles-shape.test.js guards against tile-config drift.
 *
 * The component-render layer (BlueprintTile + BlueprintGlyphs) imports
 * the data and the glyph nodes; this file does NOT import React so it
 * stays pure-JS testable.
 */

export const TILES = [
  {
    key: "thought_engineering",
    to: "/blueprints/thought-engineering",
    glyphName: "CMGlyph",
    title: "Thought Engineering",
    blurb:
      "Strategy & design. Engineer the whole encounter before the first word — the impression, the molecular draws that pull this person, the logic gaps to close, and when to time the close. The framework, made operational.",
    tierLabel: "Pro plan",
    featureKey: "thought_engineering",
    ctaLabel: "Engineer the plan",
    isNew: true,
  },
  {
    key: "conflict_mediation",
    to: "/blueprints/conflict-mediation",
    glyphName: "CMGlyph",
    title: "Conflict Mediation",
    blurb:
      "When a conversation has turned adversarial and you have to find the path through. Prepared for the person who has to hold the room.",
    tierLabel: "Pro plan",
    featureKey: "pre_meeting_conflict_mediation",
    isNew: true,
  },
  {
    key: "pre_meeting",
    to: "/blueprints/pre-meeting",
    glyphName: "PreMeetingGlyph",
    title: "Pre-Meeting Brief",
    blurb:
      "Walk into any high-stakes meeting — a pitch, a negotiation, a tough conversation — already knowing how the other person decides and what to open with.",
    tierLabel: "Core plan",
    featureKey: "pre_meeting_standard",
    isNew: false,
  },
  {
    key: "operating_manual",
    to: "/profiles", // Operating Manuals live per-Profile; the list is the entry point
    glyphName: "OperatingManualGlyph",
    title: "Playbook",
    blurb:
      "A living read on how a specific person works and decides. Pull it up before any conversation with them.",
    tierLabel: "Core plan",
    featureKey: "operating_manual",
    ctaLabel: "Open people",
    isNew: false,
  },
  {
    key: "hiring",
    to: "/blueprints/counterpart-breakdown",
    glyphName: "HiringGlyph",
    title: "Counterpart Breakdown",
    blurb:
      "Drop in a bio, profile, or background. Get who they likely are, what moves them, and how to approach the first conversation. Opens as a one-screen card; the full briefing is one click away.",
    tierLabel: "Pro plan",
    featureKey: "candidate_breakdown",
    isNew: false,
  },
  {
    key: "relational",
    to: "/blueprints/relational",
    glyphName: "RelationalGlyph",
    title: "Relationship Read",
    blurb:
      "When two people are stuck or clashing — the translation table for what each one tends to mishear about the other.",
    tierLabel: "Core plan",
    featureKey: "relational_blueprint",
    isNew: false,
  },
  {
    key: "negotiation",
    to: "/blueprints/negotiation",
    glyphName: "PreMeetingGlyph",
    title: "Negotiation Blueprint",
    blurb:
      "Read the person across the table before you sit down — what they're really optimizing for, where their give is, how they concede, and your move sequence with a concession ladder.",
    tierLabel: "Pro plan",
    featureKey: "negotiation_blueprint",
    isNew: true,
  },
  {
    key: "common_ground",
    to: "/blueprints/common-ground",
    glyphName: "RelationalGlyph",
    title: "Common Ground",
    blurb:
      "Find your fastest way in with one person — a real Common Goal to align on and a real Common Enemy to unite against, and which lever to lead with given how they're wired.",
    tierLabel: "Pro plan",
    featureKey: "common_ground",
    ctaLabel: "Explore alignment",
    isNew: true,
  },
  {
    key: "team",
    to: "/blueprints/room",
    glyphName: "TeamGlyph",
    title: "Room Blueprint",
    blurb:
      "Read a whole room before you walk in — a buying committee, panel, or leadership team. Who decides, who blocks, who to win first, and the order to work them.",
    tierLabel: "Pro plan",
    featureKey: "team_blueprint",
    isNew: true,
  },
];
